// components/Cart/Cart.js
import React, { useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import './Cart.css';

const Cart = ({ setCurrentPage, setPageData }) => {
  const { 
    cart, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    getCartSummary, 
    formatPrice 
  } = useCart();

  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const summary = getCartSummary();

  // Handle quantity change
  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(item.id, item.type);
    } else {
      updateQuantity(item.id, item.type, newQuantity);
    }
  };

  // Handle remove item
  const handleRemoveItem = (item) => {
    removeFromCart(item.id, item.type);
  };

  // Handle clear cart
  const handleClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
  };

  // Navigate to checkout
  const handleProceedToCheckout = () => {
    setCurrentPage('checkout');
  };

  // Navigate to item details
  const handleItemClick = (item) => {
    setPageData({
      itemId: item.id,
      itemType: item.type
    });
    setCurrentPage('details');
  };

  // Continue shopping
  const handleContinueShopping = () => {
    setCurrentPage('products');
  };

  if (summary.isEmpty) {
    return (
      <div className="cart-page">
        <section className="cart-header">
          <div className="container">
            <h1 className="page-title">Shopping Cart</h1>
          </div>
        </section>

        <section className="cart-content section">
          <div className="container">
            <div className="empty-cart">
              <div className="empty-cart-icon">🛒</div>
              <h2>Your cart is empty</h2>
              <p>Start shopping to add items to your cart</p>
              <div className="empty-cart-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setCurrentPage('products')}
                >
                  Shop Products
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage('services')}
                >
                  Browse Services
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {/* Cart Header */}
      <section className="cart-header">
        <div className="container">
          <h1 className="page-title">Shopping Cart</h1>
          <p className="cart-summary">
            {summary.itemCount} item{summary.itemCount !== 1 ? 's' : ''} • {formatPrice(summary.totalAmount)}
          </p>
        </div>
      </section>

      {/* Cart Content */}
      <section className="cart-content section">
        <div className="container">
          <div className="cart-layout">
            {/* Cart Items */}
            <div className="cart-items">
              <div className="cart-items-header">
                <h2>Cart Items ({summary.itemCount})</h2>
                <button 
                  className="btn btn-link clear-cart-btn"
                  onClick={() => setShowClearConfirm(true)}
                >
                  Clear Cart
                </button>
              </div>

              <div className="cart-items-list">
                {cart.items.map((item, index) => (
                  <div key={`${item.type}-${item.id}-${index}`} className="cart-item">
                    {/* Item Image */}
                    <div className="cart-item-image">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.title}
                          onClick={() => handleItemClick(item)}
                          className="clickable"
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          {item.type === 'product' ? '📦' : '⚙️'}
                        </div>
                      )}
                      {item.isOnSale && (
                        <div className="sale-badge">Sale</div>
                      )}
                    </div>

                    {/* Item Details */}
                    <div className="cart-item-details">
                      <div className="item-header">
                        <h3 
                          className="item-title clickable"
                          onClick={() => handleItemClick(item)}
                        >
                          {item.title}
                        </h3>
                        <span className="item-type">
                          {item.type === 'product' ? 'Product' : 'Service'}
                        </span>
                      </div>

                      {item.category && (
                        <p className="item-category">{item.category}</p>
                      )}

                      <div className="item-pricing">
                        {item.isOnSale && item.originalPrice && item.originalPrice > item.price ? (
                          <>
                            <span className="original-price">{formatPrice(item.originalPrice)}</span>
                            <span className="sale-price">{formatPrice(item.price)}</span>
                          </>
                        ) : (
                          <span className="current-price">{formatPrice(item.price)}</span>
                        )}
                        <span className="price-per-item">each</span>
                      </div>

                      {/* Stock Status */}
                      {item.type === 'product' && (
                        <div className="stock-status">
                          {item.inStock ? (
                            <span className="in-stock">✓ In Stock</span>
                          ) : (
                            <span className="out-of-stock">⚠️ Out of Stock</span>
                          )}
                          {item.stockQuantity && (
                            <span className="stock-info">
                              {item.stockQuantity} available
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="cart-item-quantity">
                      <label>Quantity:</label>
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(item, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <input 
                          type="number" 
                          className="quantity-input"
                          value={item.quantity}
                          onChange={(e) => {
                            const qty = parseInt(e.target.value) || 1;
                            handleQuantityChange(item, qty);
                          }}
                          min="1"
                          max={item.stockQuantity || 999}
                        />
                        <button 
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(item, item.quantity + 1)}
                          disabled={item.stockQuantity && item.quantity >= item.stockQuantity}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Item Total */}
                    <div className="cart-item-total">
                      <div className="item-total-price">
                        {formatPrice(item.price * item.quantity)}
                      </div>
                      <button 
                        className="remove-item-btn"
                        onClick={() => handleRemoveItem(item)}
                        title="Remove from cart"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Summary */}
            <div className="cart-summary-section">
              <div className="cart-summary-card">
                <h3>Order Summary</h3>
                
                <div className="summary-line total">
                  <span>Total ({summary.itemCount} item{summary.itemCount !== 1 ? 's' : ''})</span>
                  <span>{formatPrice(summary.totalAmount)}</span>
                </div>

                {/* Payment Method Info */}
                <div className="payment-info">
                  <div className="payment-method">
                    <span className="payment-icon">💰</span>
                    <div>
                      <strong>Cash on Delivery (COD)</strong>
                      <p>Pay when your order arrives</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="cart-actions">
                  <button 
                    className="btn btn-primary btn-large checkout-btn"
                    onClick={handleProceedToCheckout}
                    disabled={!summary.hasProducts}
                  >
                    Proceed to Checkout
                  </button>
                  
                  {!summary.hasProducts && summary.hasServices && (
                    <div className="service-notice">
                      <p><small>⚠️ Services require consultation. Please contact us for service bookings.</small></p>
                    </div>
                  )}
                  
                  <button 
                    className="btn btn-secondary continue-shopping-btn"
                    onClick={handleContinueShopping}
                  >
                    Continue Shopping
                  </button>
                </div>

                {/* Security Badges */}
                <div className="security-badges">
                  <div className="security-badge">🔒 Secure Checkout</div>
                  <div className="security-badge">✅ Safe Payment</div>
                  <div className="security-badge">📦 Fast Delivery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clear Cart Confirmation Modal */}
      {showClearConfirm && (
        <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Clear Cart?</h3>
            <p>Are you sure you want to remove all items from your cart? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleClearCart}
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;